import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, query, where, getDocs, getDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let app, auth, db, storage;

export const initializeFirebase = () => {
  if (!app) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
  }
};

export { auth, db, storage };

export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    throw error;
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

export const createListing = async (userId, listingData, photos) => {
  try {
    const photoUrls = [];
    for (let i = 0; i < photos.length; i++) {
      const file = photos[i];
      const storageRef = ref(storage, `listings/${userId}/${Date.now()}-${i}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      photoUrls.push(url);
    }
    const docRef = await addDoc(collection(db, 'listings'), {
      ...listingData,
      userId,
      photos: photoUrls,
      createdAt: new Date(),
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const updateListing = async (listingId, userId, listingData, newPhotos) => {
  try {
    const listing = await getListing(listingId);
    
    if (listing.userId !== userId) {
      throw new Error('Unauthorized: You can only edit your own listings');
    }

    let photoUrls = listing.photos || [];

    if (newPhotos && newPhotos.length > 0) {
      for (let i = 0; i < newPhotos.length; i++) {
        const file = newPhotos[i];
        const storageRef = ref(storage, `listings/${userId}/${Date.now()}-${i}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        photoUrls.push(url);
      }
    }

    const docRef = doc(db, 'listings', listingId);
    await updateDoc(docRef, {
      ...listingData,
      photos: photoUrls,
      updatedAt: new Date(),
    });

    return listingId;
  } catch (error) {
    throw error;
  }
};

export const deleteListing = async (listingId, userId) => {
  try {
    const listing = await getListing(listingId);
    
    if (listing.userId !== userId) {
      throw new Error('Unauthorized: You can only delete your own listings');
    }

    // Delete photos from storage
    if (listing.photos && listing.photos.length > 0) {
      for (const photoUrl of listing.photos) {
        try {
          const photoRef = ref(storage, photoUrl);
          await deleteObject(photoRef);
        } catch (error) {
          // Continue even if photo deletion fails
          console.warn('Failed to delete photo:', error);
        }
      }
    }

    // Delete document
    const docRef = doc(db, 'listings', listingId);
    await deleteDoc(docRef);

    return listingId;
  } catch (error) {
    throw error;
  }
};

export const getListings = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'listings'));
    const listings = [];
    querySnapshot.forEach((doc) => {
      listings.push({ id: doc.id, ...doc.data() });
    });
    return listings;
  } catch (error) {
    throw error;
  }
};

export const getListing = async (id) => {
  try {
    const docRef = doc(db, 'listings', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export const getUserListings = async (userId) => {
  try {
    const q = query(collection(db, 'listings'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const listings = [];
    querySnapshot.forEach((doc) => {
      listings.push({ id: doc.id, ...doc.data() });
    });
    return listings;
  } catch (error) {
    throw error;
  }
};

export const getBookedDates = async (listingId) => {
  try {
    const q = query(collection(db, 'bookings'), where('listingId', '==', listingId));
    const querySnapshot = await getDocs(q);
    const bookedDates = [];
    querySnapshot.forEach((doc) => {
      const booking = doc.data();
      const start = new Date(booking.checkIn);
      const end = new Date(booking.checkOut);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        bookedDates.push(d.toISOString().split('T')[0]);
      }
    });
    return bookedDates;
  } catch (error) {
    console.warn('Could not fetch booked dates:', error);
    return []; // Return empty array if bookings collection doesn't exist yet
  }
};

export const createBooking = async (listingId, userId, checkIn, checkOut, guestName, guestEmail) => {
  try {
    const docRef = await addDoc(collection(db, 'bookings'), {
      listingId,
      userId,
      checkIn,
      checkOut,
      guestName,
      guestEmail,
      createdAt: new Date(),
      status: 'pending'
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

// Calendar Pricing & Availability
export const setCalendarDate = async (listingId, userId, date, data) => {
  try {
    const listing = await getListing(listingId);
    if (listing.userId !== userId) {
      throw new Error('Unauthorized: You can only manage your own listings');
    }

    const docRef = doc(db, 'calendarData', `${listingId}_${date}`);
    if (data.blocked || data.price) {
      await updateDoc(docRef, { ...data, listingId, date, updatedAt: new Date() });
    } else {
      await deleteDoc(docRef);
    }
    return date;
  } catch (error) {
    throw error;
  }
};

export const getCalendarData = async (listingId, startDate, endDate) => {
  try {
    const q = query(
      collection(db, 'calendarData'),
      where('listingId', '==', listingId)
    );
    const querySnapshot = await getDocs(q);
    const calendarData = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      calendarData[data.date] = { blocked: data.blocked || false, price: data.price || null };
    });
    return calendarData;
  } catch (error) {
    console.warn('Could not fetch calendar data:', error);
    return {};
  }
};

// Messaging
export const sendMessage = async (conversationId, userId, senderName, messageText) => {
  try {
    const docRef = await addDoc(collection(db, 'messages'), {
      conversationId,
      userId,
      senderName,
      text: messageText,
      createdAt: new Date(),
      read: false
    });
    
    // Update conversation lastMessage
    const convRef = doc(db, 'conversations', conversationId);
    await updateDoc(convRef, {
      lastMessage: messageText,
      lastMessageTime: new Date()
    });

    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const getMessages = async (conversationId) => {
  try {
    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId)
    );
    const querySnapshot = await getDocs(q);
    const messages = [];
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    return messages.sort((a, b) => a.createdAt - b.createdAt);
  } catch (error) {
    throw error;
  }
};

export const getConversations = async (userId) => {
  try {
    const q = query(
      collection(db, 'conversations'),
      where('hostId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const conversations = [];
    querySnapshot.forEach((doc) => {
      conversations.push({ id: doc.id, ...doc.data() });
    });
    return conversations.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
  } catch (error) {
    console.warn('Could not fetch conversations:', error);
    return [];
  }
};

export const createConversation = async (hostId, guestId, listingId, guestName, guestEmail, listingTitle) => {
  try {
    const existingQ = query(
      collection(db, 'conversations'),
      where('hostId', '==', hostId),
      where('guestId', '==', guestId),
      where('listingId', '==', listingId)
    );
    const existing = await getDocs(existingQ);
    if (!existing.empty) {
      return existing.docs[0].id;
    }

    const docRef = await addDoc(collection(db, 'conversations'), {
      hostId,
      guestId,
      listingId,
      guestName,
      guestEmail,
      listingTitle,
      createdAt: new Date(),
      lastMessage: '',
      lastMessageTime: new Date()
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};
